import { useMutation } from '@apollo/client/react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Heading,
  HStack,
  Input,
  NativeSelect,
  SimpleGrid,
  Stack,
  VStack,
  Field,
} from '@chakra-ui/react'
import { UploadButton } from '@src/components/sco/Account/UploadButton'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { LocationInput, LocationInputPlace } from '@src/components/sco/LocationInput'
import { CheckCircleIcon } from '@src/components/sco/icons'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import annual_revenue from '@src/constants/annual-revenue'
import funding_ranges from '@src/constants/funding-ranges'
import { FUNDING_STAGES } from '@src/constants/funding-stages'
import headcount_ranges from '@src/constants/headcount-ranges'
import industries from '@src/constants/industries'
import { profileVisibilities } from '@src/constants/profile-visibilities'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import { scoBackendUrl } from '@src/lib/startups-url'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { LuFile } from 'react-icons/lu'

type CompanyProfileFormFields = {
  annualRevenueKey?: string
  companyId?: string
  elevatorPitch?: string
  fundingRaisedKey?: string
  fundingStageKey?: string
  headcountKey?: string
  industryKey?: string
  location?: string
  logoUrl?: string
  name?: string
  pitchDeckUrl?: string
  profileVisibilityKey?: string
  yearFounded?: string
  stageKey?: string
  url?: string
}

type CompanyProfileFormProps = GenericFormProps<CompanyProfileFormFields> & {
  submitControlled?: boolean
  submitTrigger?: number
  title?: string
  showAdditionalFields?: boolean
}

export const CompanyProfileForm = (props: CompanyProfileFormProps) => {
  const [formErrorMessage, setFormErrorMessage] = useState<string>()
  const [formSuccessMessage, setFormSuccessMessage] = useState<string>()
  const { getBreakpointValue } = useBreakpointContext()
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const [upsertCompany] = useMutation(gql`
    mutation ($input: UpsertCompanyInput!) {
      upsertCompany(input: $input) {
        user {
          id
          company {
            id
            annualRevenueKey
            companyId
            elevatorPitch
            fundingRaisedKey
            fundingStageKey
            headcountKey
            industryKey
            location
            logoUrl
            name
            pitchDeckUrl
            profileVisibilityKey
            stageKey
            url
          }
        }
      }
    }
  `)
  const form = useForm<CompanyProfileFormFields>({
    defaultValues: {
      annualRevenueKey: currentUser?.company?.annualRevenueKey,
      companyId: currentUser?.company?.companyId,
      elevatorPitch: currentUser?.company?.elevatorPitch,
      fundingRaisedKey: currentUser?.company?.fundingRaisedKey,
      fundingStageKey: currentUser?.company?.fundingStageKey,
      headcountKey: currentUser?.company?.headcountKey,
      industryKey: currentUser?.company?.industryKey,
      location: currentUser?.company?.location,
      logoUrl: currentUser?.company?.logoUrl,
      pitchDeckUrl: currentUser?.company?.pitchDeckUrl,
      profileVisibilityKey: currentUser?.company?.profileVisibilityKey,
      name: currentUser?.company?.name,
      yearFounded: currentUser?.company?.yearFounded,
      stageKey: currentUser?.company?.stageKey,
      url: currentUser?.company?.url,
    },
  })
  const [locationPlace, setLocationPlace] = useState<LocationInputPlace>(
    currentUser?.company?.location
      ? {
          placeId: 'default',
          title: currentUser.company.location,
        }
      : null
  )
  const logoUrl = form.watch('logoUrl')

  const handleSubmit = useCallback(
    values =>
      processSubmit({
        values,
        props,
        setFormErrorMessage,
        action: async (values: CompanyProfileFormFields) => {
          const result = await upsertCompany({
            variables: {
              input: {
                ...values,
                yearFounded: values.yearFounded ? `${parseInt(values.yearFounded)}` : null,
                ...(locationPlace && locationPlace.placeId != 'default'
                  ? {
                      location: locationPlace.title,
                      locationCity: locationPlace.city,
                      locationState: locationPlace.state,
                      locationCountryKey: locationPlace.countryISO || locationPlace.country,
                      locationPlaceId: locationPlace.placeId,
                      locationLatitude: locationPlace.latitude,
                      locationLongitude: locationPlace.longitude,
                    }
                  : {}),
              },
            },
          })
          return result
        },
        afterSuccess: async (values, result) => {
          setFormSuccessMessage('Profile Saved')
          setTimeout(() => setFormSuccessMessage(null), 3000)
          await refetchCurrentUser()
        },
      }),
    [locationPlace?.placeId]
  )

  useEffect(() => {
    if (props.submitTrigger) {
      form.handleSubmit(handleSubmit)()
    }
  }, [props.submitTrigger])

  return (
    <FormContainer {...props.containerProps}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <VStack align={'stretch'} gap={4} justify={'stretch'}>
          {props.title && <Heading>{props.title}</Heading>}

          {formErrorMessage && (
            <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box marginLeft={'1em'}>{formErrorMessage}</Box>
            </Alert.Root>
          )}

          <SimpleGrid columns={getBreakpointValue([1, 2])} gap={4}>
            <Field.Root invalid={!!form.formState.errors.name}>
              <Field.Label>Company Name</Field.Label>
              <Input placeholder={'Acme Inc.'} type={'text'} {...form.register('name', {})} />
              <Field.ErrorText>{form.formState.errors.name?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={!!form.formState.errors.url}>
              <Field.Label>Website URL</Field.Label>
              <Input placeholder={'https://acme.com'} type={'text'} {...form.register('url', {})} />
              <Field.ErrorText>{form.formState.errors.url?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <Field.Root invalid={!!form.formState.errors.elevatorPitch}>
            <Field.Label>Elevator Pitch</Field.Label>
            <AutoResizeTextarea
              placeholder={'Briefly summarize what your startup does'}
              {...form.register('elevatorPitch', {})}
            />
            <Field.ErrorText>{form.formState.errors.elevatorPitch?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root>
            <Field.Label>Location</Field.Label>
            <LocationInput
              placeTypes={['(cities)']}
              defaultValue={
                currentUser?.location ? { placeId: 'default', title: currentUser.location } : null
              }
              onChange={place => {
                setLocationPlace(place)
              }}
            />
          </Field.Root>

          <SimpleGrid columns={getBreakpointValue([1, 2])} gap={'1.5em'}>
            <Field.Root invalid={!!form.formState.errors.name}>
              <Field.Label>Industry</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field placeholder={'Select...'} {...form.register('industryKey', {})}>
                  {industries.map(i => (
                    <option key={i.key} value={i.key}>
                      {i.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.name?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={!!form.formState.errors.yearFounded}>
              <Field.Label>Year Founded</Field.Label>
              <Input type={'number'} {...form.register('yearFounded', {})} />
            </Field.Root>
          </SimpleGrid>
          <SimpleGrid columns={getBreakpointValue([1, 2])} gap={'1.5em'}>
            <Field.Root invalid={!!form.formState.errors.headcountKey}>
              <Field.Label>Team Size</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field placeholder={'Select...'} {...form.register('headcountKey', {})}>
                  {headcount_ranges.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.headcountKey?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={!!form.formState.errors.annualRevenueKey}>
              <Field.Label>Annual Revenue</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field placeholder={'Select...'} {...form.register('annualRevenueKey', {})}>
                  {annual_revenue.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.annualRevenueKey?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <SimpleGrid columns={getBreakpointValue([1, 2])} gap={'1.5em'}>
            <Field.Root invalid={!!form.formState.errors.fundingStageKey}>
              <Field.Label>Current Funding Stage</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field placeholder={'Select...'} {...form.register('fundingStageKey', {})}>
                  {FUNDING_STAGES.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.title}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.fundingStageKey?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={!!form.formState.errors.fundingRaisedKey}>
              <Field.Label>Funding Raised</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field placeholder={'Select...'} {...form.register('fundingRaisedKey', {})}>
                  {funding_ranges.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.fundingRaisedKey?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <Field.Root>
            <Field.Label>Company Logo</Field.Label>
            <Box
              bgColor={'containerSecondary'}
              border={'1px solid'}
              borderColor={'actionPrimaryAlpha.200'}
              borderRadius={'md'}
              padding={'1em 1.5em'}
            >
              <Stack align={'center'} direction={getBreakpointValue(['column', 'row'])} justify={'center'}>
                <Box textAlign={'center'}>
                  <Avatar.Root borderRadius={'lg'} size={'xl'}>
                    <Avatar.Fallback borderRadius={'lg'} name={[form.getValues().name].join(' ')} />
                    <Avatar.Image borderRadius={'lg'} src={logoUrl} />
                  </Avatar.Root>
                </Box>
                <HStack flexGrow={1} justify={'center'} width={'full'}>
                  <Box>
                    <UploadButton
                      afterError={err => alert(`Upload failed: ${err.toString()}`)}
                      afterSuccess={url => form.setValue('logoUrl', url)}
                      allowedExtensions={['jpeg', 'jpg', 'png']}
                      uploadUrl={scoBackendUrl('/assets/upload-image')}
                    >
                      Upload an Image
                    </UploadButton>
                  </Box>
                  <Box>or</Box>
                  <Button variant={'outline'} onClick={() => form.setValue('logoUrl', null)}>
                    Remove
                  </Button>
                </HStack>
              </Stack>
            </Box>{' '}
          </Field.Root>

          <Field.Root display={'none'}>
            <Field.Label>Pitch Deck</Field.Label>
            <Box
              bgColor={'containerSecondary'}
              border={'1px solid'}
              borderColor={'actionPrimaryAlpha.200'}
              borderRadius={'md'}
              padding={'1em 1.5em'}
            >
              <Stack align={'center'} direction={getBreakpointValue(['column', 'row'])} justify={'center'}>
                <Box textAlign={'center'}>
                  <LuFile />
                </Box>
                <HStack flexGrow={1} width={'full'}>
                  <Box>
                    <UploadButton
                      afterError={err => alert(`Upload failed: ${err.toString()}`)}
                      afterSuccess={url => form.setValue('logoUrl', url)}
                      allowedExtensions={['jpeg', 'jpg', 'png']}
                      buttonProps={{}}
                      uploadUrl={scoBackendUrl('/assets/upload-document')}
                    >
                      Upload PDF or PPT
                    </UploadButton>
                  </Box>
                  <Box>or</Box>
                  <Button variant={'outline'} onClick={() => form.setValue('logoUrl', null)}>
                    Remove
                  </Button>
                </HStack>
              </Stack>
            </Box>
          </Field.Root>

          {props.showAdditionalFields && (
            <>
              <Field.Root invalid={!!form.formState.errors.profileVisibilityKey}>
                <Field.Label>Profile Visibility</Field.Label>

                <NativeSelect.Root>
                  <NativeSelect.Field {...form.register('profileVisibilityKey', {})}>
                    {profileVisibilities.map(item => (
                      <option key={item.key} value={item.key}>
                        {item.title}
                        {': '}
                        {item.description}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>

                <Field.ErrorText>{form.formState.errors.profileVisibilityKey?.message}</Field.ErrorText>
              </Field.Root>
            </>
          )}

          <Button
            display={props.submitControlled ? 'none' : null}
            loading={form.formState.isSubmitting}
            type={'submit'}
            variant={'cta'}
          >
            {formSuccessMessage ? (
              <>
                <CheckCircleIcon marginRight={'1em'} /> {formSuccessMessage}
              </>
            ) : (
              'Save Company Profile'
            )}
          </Button>
        </VStack>
      </form>
    </FormContainer>
  )
}
